import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

// Funkcja liniowa: oblicz wartość f(x0) dla f(x)=ax+b
export function generateTask() {
  const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  let a = 0;
  while (a === 0) a = rand(-6, 6); // a != 0

  const b = rand(-8, 8);
  const x0 = rand(-6, 6);

  const fx = a * x0 + b;

  return {
    type: 'LinearComputeValueTask',
    data: { a, b, x0, fx },
  };
}

function formatAx(a) {
  if (a === 1) return 'x';
  if (a === -1) return '-x';
  return `${a}x`;
}

function formatSignedNumber(n) {
  return n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`;
}

export function renderTask({ data }) {
  const { a, b, x0 } = data;

  const ax = formatAx(a);
  const bPart = b === 0 ? '' : ` ${formatSignedNumber(b)}`;

  return (
    <>
      <p>
        Dana jest funkcja liniowa <InlineMath math={`f(x) = ${ax}${bPart}`} />.
      </p>
      <p>
        Oblicz <InlineMath math={`f(${x0})`} />.
      </p>
    </>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { x0 } = data;

  return (
    <div className="flex items-center gap-2">
      <span>
        <InlineMath math={`f(${x0}) = `} />
      </span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={`f(${x0})`}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;

  return Math.abs(given - data.fx) < 0.01;
}

/**
 * Kompatybilne wyświetlanie:
 * - renderUserAnswer(userAnswer)
 * - renderUserAnswer(task, userAnswer)
 */
export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  let task = null;
  let userAnswer = null;

  if (maybeUserAnswer !== undefined) {
    task = taskOrUserAnswer;
    userAnswer = maybeUserAnswer;
  } else {
    userAnswer = taskOrUserAnswer;
  }

  if (!userAnswer || typeof userAnswer !== 'object' || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const x0 = task?.data?.x0;
  const left = x0 !== undefined && x0 !== null ? `f(${x0})` : 'f(x_0)';

  return <InlineMath math={`${left} = ${formatLatexPreview(userAnswer.raw)}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f(${data.x0}) = ${data.fx}`} />;
}

export function renderExplanation({ data }) {
  const { a, b, x0, fx } = data;

  const bSigned = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const bSignedRaw = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;

  return (
    <div className="space-y-4">
      {/* Wzór */}
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Wzór funkcji</div>
        <BlockMath math={`f(x) = ${a}x ${bSigned}`} />
      </div>

      {/* Krok 1 */}
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Podstawiamy</div>
        <BlockMath math={`f(${x0}) = ${a} \\cdot (${x0}) ${bSignedRaw}`} />
      </div>

      {/* Krok 2 */}
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Obliczamy</div>
        <BlockMath math={`f(${x0}) = ${fx}`} />
      </div>
    </div>
  );
}